import React, {useEffect, useState} from "react";
import styled from "styled-components";
import { useAppDispatch } from "../../app/hooks";
import { setOldExpectedDelayAlertAction } from "../../app/reducers/alert-slice";
import theme from "../../style/theme";
import { OperationAlertDeliveryGroup } from "../../types/alert/operation-alert-delivery-group";
import TimeLineIcon from "../icons/TimeLineIcon";
import { ExpectedDelayContentsBodyItem } from "./ExpectedDelayContentsBodyItem";
import { AlertPanelBasicHeader } from "./AlertPanelBasicHeader";
import {sortAscAlertGroups, sortDescAlertGroups} from "../../utils/alert/alert-sort-util";
import {AlertDeliveryGroupListAscIcon, AlertDeliveryGroupListSortIcon} from "../../utils/alert/alert-sort-icon";

interface Props {
  newAlerts: OperationAlertDeliveryGroup[];
  allAlerts: OperationAlertDeliveryGroup[];
  oldAlerts: OperationAlertDeliveryGroup[];
}

export const ExpectedDelayContents: React.FC<Props> = ({
  newAlerts,
  allAlerts,
    oldAlerts
}) => {
  const dispatch = useAppDispatch();
  const [isDesc, setIsDesc] = useState(true);

  const toggleSortOrder = () => {
    setIsDesc((prevOrder) => !prevOrder);
  };

  useEffect(() => {
    return () => {
      dispatch(setOldExpectedDelayAlertAction({ alerts: [...newAlerts, ...oldAlerts] }));
    };
  }, []);

  return (
    <DelayListBox>
      <AlertPanelBasicHeader
        text1="지연 예상 알림"
        text2="요청시간"
        text3="예상시간"
      />

      <DelayListTable>
        {newAlerts.length > 0 && (
          <>
            <DelayDeliveryGroupListTitle>
              <DelayDeliveryGroupListItem>새로운</DelayDeliveryGroupListItem>
              {isDesc ? (
                  <AlertDeliveryGroupListSortIcon />
              ) : (
                  <AlertDeliveryGroupListAscIcon />
              )}
              <DelayDeliveryGroupSortButton onClick={toggleSortOrder}>
                요청시간순{isDesc ? "(내림차순)" : "(오름차순)"}
              </DelayDeliveryGroupSortButton>
            </DelayDeliveryGroupListTitle>
            {[...newAlerts].sort(isDesc ? sortDescAlertGroups : sortAscAlertGroups).map((newAlert) => (
              <ExpectedDelayContentsBodyItem isNew={true} alert={newAlert} />
            ))}
          </>
        )}
        <DelayDeliveryGroupListTitle>
          <DelayDeliveryGroupListItem>전체</DelayDeliveryGroupListItem>
          {isDesc ? (
              <AlertDeliveryGroupListSortIcon />
          ) : (
              <AlertDeliveryGroupListAscIcon />
          )}
          <DelayDeliveryGroupSortButton onClick={toggleSortOrder}>
            요청시간순{isDesc ? "(내림차순)" : "(오름차순)"}
          </DelayDeliveryGroupSortButton>
        </DelayDeliveryGroupListTitle>
        {allAlerts.length > 0 &&
          [...allAlerts].sort(isDesc ? sortDescAlertGroups : sortAscAlertGroups).map((alert) => {
            return <ExpectedDelayContentsBodyItem isNew={false} alert={alert} />;
          })}
        {allAlerts.length === 0 && (
          <DelayListEmpty>현재 지연 예상 배송이 없습니다</DelayListEmpty>
        )}
      </DelayListTable>
    </DelayListBox>
  );
};

const DelayListBox = styled.div`
  position: absolute;
  top: 52px;
  right: 0px;
  background: ${theme.palette.primitives.white};
  width: 390px;
  min-height: 122px;
  border-radius: 10px;
  border: 1px solid ${theme.palette.primitives.gray300};
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
`;

const DelayListTable = styled.div`
  overflow-y: auto;
  min-height: 78px;
  border-radius: 0px 0px 8px 8px;
  max-height: 291px;

  &::-webkit-scrollbar {
    width: 8px; /* 스크롤바의 너비 */
  }

  &::-webkit-scrollbar-thumb {
    height: 30%; /* 스크롤바의 길이 */
    background: ${theme.palette.primitives.gray300};

    border-radius: 10px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.palette.primitives.gray50};
    /* All/Gray 100 */

    border: 1px solid ${theme.palette.primitives.gray100};
    border-bottom-right-radius: 8px;
  }
`;

const DelayDeliveryGroupListTitle = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 7px 7px 16px;
  font-weight: 600;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.palette.primitives.gray400};
`;

const DelayDeliveryGroupListItem = styled.div`
  margin-right: 7px;
`;

const DelayDeliveryGroupSortButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-weight: 400;
  font-size: 12px;
  color: ${theme.palette.primitives.gray400};
  padding: 0;
`;

const DelayListDeliveryGroup = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  padding: 11px 20px 13px 32px;
  box-sizing: border-box;
  cursor: pointer;

  & > div:first-child {
    width: 149px;
    flex: 2;
    text-align: left;
    color: ${theme.palette.primitives.gray500};
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
  }

  &:hover {
    background-color: ${theme.palette.primitives.gray50};
  }
`;

const DelayListAlertCircle = styled.span`
  position: absolute;
  width: 8px;
  height: 8px;
  top: 16px;
  left: 16px;
  border-radius: 999px;
  background-color: ${theme.palette.primitives.red500};
`;

const DelayListTitleBox = styled.div`
  width: 150px;
  & > p:first-child {
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.palette.primitives.black};
    margin-bottom: 4px;
  }

  & > p:last-child {
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: ${theme.palette.primitives.gray600};
  }

  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 20px;
  }
`;

const DelayListTimeBox = styled.div`
  text-align: center;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  flex: 1;
  display: flex;
  justify-content: center;

  & > .requestTime {
    color: ${theme.palette.primitives.gray600};
  }

  & > .delayTime {
    color: ${theme.palette.primitives.red500};
  }
`;

const DelayTimeIcon = styled(TimeLineIcon)`
  width: 17px;
  height: 17px;
  margin-right: 2px;
  cursor: pointer;

  &.requestTime {
    fill: ${theme.palette.primitives.gray400};
  }

  &.delayTime {
    fill: ${theme.palette.primitives.red500};
  }
`;

const DelayListEmpty = styled.div`
  display: flex;
  align-items: center;
  padding: 21px 24px 21px 32px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.primitives.gray400};
`;
